import { Component, inject, OnDestroy, OnInit, ViewEncapsulation } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { MsalBroadcastService, MsalService } from "@azure/msal-angular";
import { TranslocoPipe } from "@jsverse/transloco";
import { AzureAdService } from "app/core/services/azure-ad.service";
import { ButtonModule } from "primeng/button";
import { ToastModule } from "primeng/toast";
import { filter, Subject, take, takeUntil } from "rxjs";

import { InteractionStatus } from "@azure/msal-browser";
import { NotificationService } from "@services/notification.service";
import { AnimateOnScrollModule } from "primeng/animateonscroll";

@Component({
    selector: "login-component",
    imports: [TranslocoPipe, ButtonModule, ToastModule, AnimateOnScrollModule],
    templateUrl: "./login.component.html",
    styleUrls: ["./login.component.scss"],
    encapsulation: ViewEncapsulation.None
})
export class LoginComponent implements OnInit, OnDestroy {
    private readonly _destroying$ = new Subject<void>();

    private msalService = inject(MsalService);
    private broadcastService = inject(MsalBroadcastService);
    private router = inject(Router);
    private route = inject(ActivatedRoute);
    private azureAdService = inject(AzureAdService);
    private notification = inject(NotificationService);

    // Chave em window.sessionStorage (não o SessionStorageService, que é em memória e não
    // sobrevive ao reload do redirect de login). Preserva a rota solicitada através do
    // round-trip completo para o Azure AD.
    private static readonly RETURN_URL_KEY = "aias.auth.returnUrl";

    async ngOnInit() {
        try {
            // Se chegamos ao /login com returnUrl (guard preservou a rota solicitada), persiste
            // em window.sessionStorage ANTES de qualquer redirect, para não depender do query
            // param sobreviver ao round-trip do loginRedirect.
            const incomingReturnUrl = this.route.snapshot.queryParams["returnUrl"];
            if (incomingReturnUrl) {
                window.sessionStorage.setItem(LoginComponent.RETURN_URL_KEY, incomingReturnUrl);
            }

            if (this.msalService.instance.getAllAccounts().length > 0) {
                this.azureAdService.userVerify();
                this.navigateToTarget();
            } else {
                this.broadcastService.inProgress$
                    .pipe(
                        filter((status: InteractionStatus) => status === InteractionStatus.None),
                        takeUntil(this._destroying$)
                    )
                    .pipe(take(1))
                    .subscribe(() => {
                        if (this.msalService.instance.getAllAccounts().length > 0) {
                            this.azureAdService.userVerify();
                            this.navigateToTarget();
                        }
                    });
            }
        } catch (error) {
            this.router.navigate(["/login"]);
            this.notification.serviceError(error);
        }
    }

    async login() {
        this.azureAdService.login();
    }

    openContactPage() {
        window.open("https://iatec.com/pt/fale-conosco/", "_blank"), "noopener,noreferrer";
    }

    ngOnDestroy(): void {
        this._destroying$.next(undefined);
        this._destroying$.complete();
    }

    private navigateToTarget(): void {
        // Recupera a rota solicitada: primeiro do query param (caso ainda presente), senão do
        // window.sessionStorage (persistido antes do redirect). Consome a chave após usar.
        const fromQuery = this.route.snapshot.queryParams["returnUrl"];
        const fromStorage = window.sessionStorage.getItem(LoginComponent.RETURN_URL_KEY);
        const returnUrl = fromQuery || fromStorage;

        window.sessionStorage.removeItem(LoginComponent.RETURN_URL_KEY);

        // Só navega para a returnUrl se for uma rota interna segura (mesma origem, path relativo).
        // Evita open-redirect: nunca navegar para URL absoluta/externa vinda de parâmetro.
        const safeTarget = this.isSafeInternalUrl(returnUrl) ? returnUrl : "/Home";
        this.router.navigateByUrl(safeTarget);
    }

    /**
     * Garante que a returnUrl é um caminho interno relativo (começa com "/" e não com "//"),
     * bloqueando URLs absolutas/externas para prevenir open redirect.
     */
    private isSafeInternalUrl(url: string | null | undefined): url is string {
        return !!url && url.startsWith("/") && !url.startsWith("//") && !url.startsWith("/login");
    }
}
